import { PathLayer } from '@deck.gl/layers';
import { RacemapColors } from '@racemap/utilities/consts/common';
import type { RenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import type { Point, StarterType } from 'src/lib/player/store';
import tinycolor from 'tinycolor2';

interface Segment {
  color: [number, number, number, number];
  path: Array<[number, number, number]>;
}

function splitTrack(track: Array<Point>, timeout: number): Array<Array<Point>> {
  return Array.from(
    (function* () {
      if (track.length < 1) {
        return;
      }
      let currentSegment = [];
      let last = track[0];
      for (let i = 1; i < track.length; i++) {
        const point = track[i];
        currentSegment.push(last);
        if (timeout !== -1 && last.time + timeout < point.time) {
          yield currentSegment;
          currentSegment = [];
        }
        last = point;
      }
      currentSegment.push(last);
      yield currentSegment;
    })(),
  );
}

export default function createStarterTrackLayer(
  starterTracks: Array<[string, Array<Point>]>,
  starters: Array<StarterType>,
  renderInfoBuffer: RenderInfoBuffer,
  time: number,
  tailTimeout = -1,
  markerTimeout = -1,
): PathLayer {
  const trackSegments = [];
  for (const [starterId, points] of starterTracks) {
    if (points.length === 0) continue;
    let fullPoints = points.filter((p) => p.time <= time);
    const starterIndex = renderInfoBuffer.starters.findIndex((s) => s.id === starterId);
    if (starterIndex >= 0) {
      const lnglat = renderInfoBuffer.getLatLngOpacity(starterIndex);
      if (lnglat.opacity > 0) {
        fullPoints = [...fullPoints, { lat: lnglat.lat, lng: lnglat.lng, time, offset: 0 }];
      }
    }
    const starter = starters.find((s) => s.id === starterId);
    for (const segment of splitTrack(fullPoints, markerTimeout)) {
      trackSegments.push({
        color: getColorTuple(tinycolor(starter?.markerColor.rgba || RacemapColors.PaleBlue)),
        path: segment
          .filter((p) => tailTimeout === -1 || p.time > time - tailTimeout)
          .map((p) => [p.lng, p.lat, 1]),
      });
    }
  }
  return new PathLayer({
    id: 'starter-tracks',
    data: trackSegments,
    getPath: (segment: Segment) => segment.path,
    getColor: (segment: Segment) => segment.color,
    pickable: false,
    getWidth: 4,
    widthMinPixels: 4,
    jointRounded: true,
  });
}

const getColorTuple = (color: tinycolor.Instance): [number, number, number, number] => {
  const rgb = color.toRgb();
  return [rgb.r, rgb.g, rgb.b, Math.round(rgb.a * 255)];
};
